import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import { Button, Card, Empty, Input, Switch, Tooltip, notification } from 'antd'
import React, { useEffect, useState } from 'react'
import {
  MalindexBesok,
  MalindexConfig,
  SparaBesok,
  archiveMalindexBesok,
  getMalindexByBrukare,
  getMalindexConfig,
  saveMalindexBesok,
  setMalindexProcessId,
} from '../../api/malindex/malindexApi'
import { useApi } from '../../api/useApi'
import ViewHeader from '../../components/viewHeader/ViewHeader'
import Behovsmatning from './components/Behovsmatning'
import OversiktHub from './components/OversiktHub'
import Uppfoljning from './components/Uppfoljning'
import BesokVy from './components/BesokVy'
import { viktandeBehov } from './malindexCalc'
import { formateraDatum } from './visning'

const api = useApi()

/**
 * Målindex-orchestrator (nivå 1).
 *
 * Personnummer → config + brukarens besök. En smal brukar-linje (samma stil som
 * beslutsstöd) fästs högst upp när en brukare är vald, med samtalslägets reglage.
 *
 * SAMTALSLÄGE slås på automatiskt när ett besök startas och stängs av manuellt efter
 * besöket. Medan det är på visas inga index eller resultat någonstans – plattan sitter
 * mellan handläggaren och brukaren, och indexet ska inte synas för den enskilde
 * (spec 2.4). Registreringsflödena innehåller aldrig resultat oavsett läge; reglaget
 * styr hubbens grafer och siffror.
 */

type Vy = 'hub' | 'kartlaggning' | 'uppfoljning' | 'besok'

const Malindex: React.FC = () => {
  const [config, setConfig] = useState<MalindexConfig | null>(null)
  const [pnr, setPnr] = useState('')
  const [aktivBrukare, setAktivBrukare] = useState<string | null>(null)
  const [besok, setBesok] = useState<MalindexBesok[]>([])
  const [vy, setVy] = useState<Vy>('hub')
  const [samtalslage, setSamtalslage] = useState(false)
  const [sparar, setSparar] = useState(false)
  const [loadedStatus, setLoadedStatus] = useState<{ pnr: string; time: string } | null>(null)
  /** Utkastet som just återupptas. null = ett nytt besök. */
  const [aterupptar, setAterupptar] = useState<MalindexBesok | null>(null)
  /** Besöket som visas i sin egen vy. */
  const [valtBesokId, setValtBesokId] = useState<number | null>(null)

  /** Det pausade besöket, om det finns ett. Bara ett kan finnas åt gången. */
  const utkast = besok.find((b) => b.draft) ?? null

  /** Besöket som visas i besöksvyn, eller null om inget är valt. */
  const valtBesok = valtBesokId === null ? null : besok.find((b) => b.id === valtBesokId) ?? null

  const [notificationInstance, contextHolder] = notification.useNotification()

  useEffect(() => {
    getMalindexConfig(api)
      .then(setConfig)
      .catch(() => notificationInstance.error({ message: 'Kunde inte hämta frågekonfigurationen' }))
    // Körs en gång vid montering – configen ändras inte under sessionen.
  }, [])

  const laddaBrukare = async (p: string) => {
    const rows = await getMalindexByBrukare(api, p)
    setBesok(rows)
    setAktivBrukare(p)
    setLoadedStatus({ pnr: p, time: new Date().toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) })
    // Tillbaka i hubben är inget flöde igång – annars skulle nästa besök ärva
    // utkastets tillstånd.
    setAterupptar(null)
    setValtBesokId(null)
    setVy('hub')
  }

  const hamtaBrukare = async () => {
    const p = pnr.trim()
    if (!p) return
    try {
      await laddaBrukare(p)
    } catch {
      notificationInstance.error({ message: 'Kunde inte hämta brukarens besök' })
    }
  }

  /** Startar ett besöksflöde och slår på samtalsläget – plattan ska vara ren i rummet. */
  const startaBesok = (nyVy: Vy) => {
    setAterupptar(null)
    setSamtalslage(true)
    setVy(nyVy)
  }

  /**
   * Återupptar det pausade besöket. Flödet väljs av utkastets egen typ, inte av
   * vilken knapp som trycktes – ett pausat uppföljningsbesök ska fortsätta som en
   * uppföljning även om det återupptas veckor senare.
   */
  const aterupptaUtkast = () => {
    if (!utkast) return
    setAterupptar(utkast)
    setSamtalslage(true)
    setVy(utkast.besokstyp === 'kartlaggning' ? 'kartlaggning' : 'uppfoljning')
  }

  /**
   * Raderar det pausade besöket ur handläggarens vy. Raden arkiveras i stället för att
   * tas bort – ingenting i den här appen försvinner på riktigt, och `getByBrukare`
   * filtrerar bort arkiverade rader.
   */
  const raderaBesok = async () => {
    if (!utkast || !aktivBrukare) return
    try {
      await archiveMalindexBesok(api, utkast.id)
      await laddaBrukare(aktivBrukare)
      notificationInstance.success({ message: 'Besöket raderades' })
    } catch {
      notificationInstance.error({ message: 'Kunde inte radera besöket' })
    }
  }

  /**
   * Raderar ett SPARAT besök, från dess egen vy.
   *
   * Skälet är inte städning: ett felaktigt besök viktar nästa mätning. `viktandeBehov`
   * väljer senaste besöket med behovssvar, och eftersom `getByBrukare` filtrerar bort
   * arkiverade rader försvinner besöket ur både vyn och viktningen.
   *
   * Vi lämnar sidan efteråt. Att stå kvar på ett besök som just raderats vore att titta
   * på något som inte längre finns.
   */
  const raderaValtBesok = async () => {
    if (!valtBesok || !aktivBrukare) return
    try {
      await archiveMalindexBesok(api, valtBesok.id)
      setValtBesokId(null)
      setVy('hub')
      await laddaBrukare(aktivBrukare)
      notificationInstance.success({ message: 'Besöket raderades' })
    } catch {
      notificationInstance.error({ message: 'Kunde inte radera besöket' })
    }
  }

  /**
   * Öppnar ett besök i sin egen vy. Besöket finns redan i minnet – ingen omhämtning
   * behövs, och rutten /app/malindex/:id lämnas oanvänd med flit: man kommer inte åt
   * någon brukare utan att först slå på personnummer, så en delbar länk vore antingen
   * verkningslös eller ett läckage.
   */
  const oppnaBesok = (id: number) => {
    setValtBesokId(id)
    setVy('besok')
  }

  /**
   * Sparar ett besök – färdigt eller pausat. Skillnaden ligger i `draft`, och den
   * avgör både vad servern gör och vad handläggaren får veta: ett utkast räknar inte
   * ut något index och ska inte utlova något resultat.
   */
  const sparaBesok = async (payload: SparaBesok) => {
    if (!aktivBrukare) return
    setSparar(true)
    try {
      const svar = await saveMalindexBesok(api, { ...payload, pnr: aktivBrukare })
      notificationInstance.success(
        payload.draft
          ? {
              message: 'Besöket pausat',
              description: 'Svaren är sparade som utkast. Återuppta från översikten när ni ses igen.',
            }
          : {
              message: 'Besöket sparat',
              description:
                svar.indexTotal !== null
                  ? 'Stäng av samtalsläget för att se resultatet.'
                  : payload.nyttaSvar
                  ? 'Inget index kunde räknas ut för besöket.'
                  : undefined,
            }
      )
      await laddaBrukare(aktivBrukare)
    } catch {
      notificationInstance.error({
        message: payload.draft ? 'Kunde inte pausa besöket' : 'Kunde inte spara besöket',
      })
    } finally {
      setSparar(false)
    }
  }

  /**
   * Fyller i ÄrendeID på ett besök som sparats utan, direkt ur besökslistan.
   *
   * Raden uppdateras lokalt i stället för att hela brukaren hämtas om. Tidigare låg
   * båda anropen i samma try, vilket gav två fel: sparandet kändes långsamt eftersom
   * spinnern satt kvar tills omhämtningen var klar, och ett misslyckat GET gav
   * felmeddelandet "Kunde inte spara" trots att sparandet hade gått igenom.
   */
  const kompletteraProcessId = async (id: number, processId: string) => {
    try {
      await setMalindexProcessId(api, id, processId)
    } catch {
      notificationInstance.error({ message: 'Kunde inte spara ÄrendeID' })
      return
    }
    setBesok((rader) => rader.map((b) => (b.id === id ? { ...b, process_id: processId } : b)))
    notificationInstance.success({ message: 'ÄrendeID sparat' })
  }

  const viktande = viktandeBehov(besok)

  return (
    <>
      {contextHolder}

      {/* Smal brukar-linje – fäst högst upp när en brukare är vald (speglar beslutsstöd). */}
      {/* Bara på översikten. Besöksvyn bär sin egen identitet i sidhuvudet, och remsan
          ovanpå den blev en andra rad som sa samma sak. */}
      {aktivBrukare && loadedStatus && vy === 'hub' && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
            padding: '10px 16px',
            // Grå i normalläge, gul när samtalsläget är på. Blått är reserverat för
            // det man kan trycka på, och brukar-linjen bär bara information. Att den
            // var blå gjorde dessutom att gulmarkeringen – den enda som betyder något
            // här – knappt syntes som en förändring.
            background: samtalslage ? '#fffbe6' : '#fafafa',
            border: `1px solid ${samtalslage ? '#ffe58f' : '#e8e8e8'}`,
            marginTop: -24,
            marginLeft: -24,
            marginRight: -24,
            marginBottom: 12,
            width: 'calc(100% + 48px)',
          }}
        >
          <span style={{ fontWeight: 600 }}>{loadedStatus.pnr}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, color: '#595959' }}>
            {viktande && (
              <span>Senaste behovsskattning {formateraDatum(viktande.created)}</span>
            )}
            <span>Hämtad {loadedStatus.time}</span>
            <Tooltip title="När samtalsläget är på visas inga index eller grafer. Slå av det först efter besöket.">
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {samtalslage ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                <span>Samtalsläge</span>
                <Switch size="small" checked={samtalslage} onChange={setSamtalslage} />
              </span>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Sidrubriken hör till översikten. Besöksvyn har en egen rubrik och en väg
          tillbaka – två rubriker ovanpå varandra säger inte mer än en.
          Brukar-linjen är däremot kvar där, för det är den som bär samtalsläget:
          utan den gick indexet inte att få fram igen efter ett avslutat besök. */}
      {vy === 'hub' && (
        <ViewHeader
          heading="Målindex"
          description="Hitta, formulera och följ upp individuella mål för brukare med hemtjänst. Ett besök kan bära en behovsskattning, en nyttoskattning eller båda."
        />
      )}

      <div className="max-w-4xl">
        {/* Brukarvalet finns bara innan en brukare är hämtad – Målindex körs en gång per
            person. Därefter bär brukar-linjen identiteten. */}
        {!aktivBrukare && (
          <Card size="small" className="mb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col">
                <label className="mb-1 text-xs">Personnummer</label>
                <Input
                  value={pnr}
                  onChange={(e) => setPnr(e.target.value)}
                  onPressEnter={hamtaBrukare}
                  placeholder="ÅÅÅÅMMDD-XXXX"
                  style={{ width: 200 }}
                />
              </div>
              <Button type="primary" onClick={hamtaBrukare} disabled={!pnr.trim()}>
                Hämta
              </Button>
            </div>
          </Card>
        )}

        {aktivBrukare && !config && (
          <Empty description="Frågorna kunde inte hämtas. Ladda om sidan – står det kvar, hör av dig till oss." />
        )}

        {aktivBrukare && config && vy === 'hub' && (
          <OversiktHub
            config={config}
            besok={besok}
            samtalslage={samtalslage}
            onNyttKartlaggningsbesok={() => startaBesok('kartlaggning')}
            onNyUppfoljning={() => startaBesok('uppfoljning')}
            onAterupptaUtkast={aterupptaUtkast}
            onRaderaBesok={raderaBesok}
            onOppnaBesok={oppnaBesok}
            onKompletteraProcessId={kompletteraProcessId}
          />
        )}

        {aktivBrukare && config && vy === 'besok' && valtBesok && (
          <BesokVy
            besok={valtBesok}
            alla={besok}
            config={config}
            samtalslage={samtalslage}
            onTillbaka={() => {
              setValtBesokId(null)
              setVy('hub')
            }}
            onRadera={raderaValtBesok}
          />
        )}

        {aktivBrukare && config && vy === 'kartlaggning' && (
          <Behovsmatning
            // Nyckeln tvingar fram ett nytt komponentliv när man växlar mellan ett
            // nytt besök och ett återupptaget – annars ligger förra flödets svar kvar
            // i state, eftersom useState-initialvärden bara läses vid montering.
            key={aterupptar ? `utkast-${aterupptar.id}` : 'nytt'}
            config={config}
            personnummer={aktivBrukare}
            utkast={aterupptar}
            onSpara={sparaBesok}
            onAvbryt={() => setVy('hub')}
            sparar={sparar}
          />
        )}

        {aktivBrukare && config && vy === 'uppfoljning' && (
          <Uppfoljning
            key={aterupptar ? `utkast-${aterupptar.id}` : 'nytt'}
            config={config}
            personnummer={aktivBrukare}
            besok={besok}
            utkast={aterupptar}
            onSpara={sparaBesok}
            onAvbryt={() => setVy('hub')}
            sparar={sparar}
          />
        )}
      </div>
    </>
  )
}

export default Malindex
